import * as React from "react";

import classNames from "classnames";

import { handleActions } from "../../../helpers/Doc/handleActions";
import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";

interface DocRatingBarProps extends DocRatingBarType, BlockNodeAll {};


function DocRatingBar(props: DocRatingBarProps) {
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);
  const starRefs = React.useRef<(HTMLSpanElement | null)[]>([]);

  const onClick = (clickValue: number, e: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    if (props.inactiveBool) { return; }
    const allActions = (props.onClick || []);
    allActions.push({
      type: "action",
      action: "setNodeKVPair",
      arguments: {
        uid: props.uid,
        key: "value",
        value: clickValue + 1,
      },
    });
    handleActions(allActions, props);
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (props.inactiveBool) return;
    if (e.key === 'Enter' || e.key === ' ') {
      onClick(i, e);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      const target = i > 0 ? i - 1 : props.maxValue - 1;
      onClick(target, e);
      starRefs.current[target]?.focus();
      setFocusedIndex(target);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      const target = i < props.maxValue - 1 ? i + 1 : 0;
      onClick(target, e);
      starRefs.current[target]?.focus();
      setFocusedIndex(target);
      e.preventDefault();
    }
  };

  const pressedIndex = props.value > 0 ? props.value - 1 : null;

  const stars = [];
  for (let i = 0; i < props.maxValue; i += 1) {
    const isActive = props.value > 0 && i < props.value;
    const isFocused = focusedIndex === i;
    const className = classNames(
      "doc-rating-bar-star",
      {"doc-rating-bar-star-active": isActive},
      {"doc-rating-bar-star-focused": isFocused}
    );
    stars.push(
      <span
        className={className}
        key={i}
        ref={el => starRefs.current[i] = el}
        tabIndex={props.inactiveBool ? -1 : 0}
        aria-label={`Rate ${i + 1} out of ${props.maxValue}`}
        role="button"
        aria-pressed={pressedIndex !== null && i === pressedIndex}
        onClick={(e) => onClick(i, e)}
        onKeyDown={(e) => onKeyDown(i, e)}
        onFocus={() => setFocusedIndex(i)}
        onBlur={() => setFocusedIndex(null)}
        style={isFocused ? {outline: '2px solid #1976d2', outlineOffset: '2px'} : {}}
      >
        ☆
      </span>
    );
  }

  const onContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!props.inactiveBool) return;
    if (e.key === 'Tab') return;
    if (
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown'
    ) {
      e.preventDefault();
    }
  };

  return (
    <div
      id={props.uid}
      data-is-node
      data-uid={props.uid}
      className={classNames("doc-rating-bar", props.uid, {inactive: props.inactiveBool})}
      style={backgroundStyleFromNode(props)}
      contentEditable={false}
      role="group"
      aria-label="Rating bar"
      aria-disabled={props.inactiveBool ? true : undefined}
      tabIndex={props.inactiveBool ? 0 : -1}
      onKeyDown={onContainerKeyDown}
    >
      {stars}
    </div>
  );
}

export default React.memo(DocRatingBar);
